import React, { useCallback, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { api } from '../auth/api';

/**
 * "Manage your data" panel rendered at the bottom of /profile.
 *
 * Exposes the two GDPR-mandated controls:
 *   • Export → GET /api/users/me/export, save as JSON
 *   • Delete → DELETE /api/users/me, then sign out
 *
 * Both actions are gated behind explicit consent (a confirm dialog for
 * export, a typed-handle confirmation for delete) so a misclick can't
 * trigger them.
 */
const AccountManagement: React.FC<{ handle: string }> = ({ handle }) => {
  const { state, signOut } = useAuth();
  const idToken = state.kind === 'authenticated' ? state.idToken : undefined;

  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [msg, setMsg] = useState('');
  const [showDeletePanel, setShowDeletePanel] = useState(false);

  const onExport = useCallback(async () => {
    if (!idToken) return;
    setExporting(true);
    setMsg('');
    try {
      const res = await api('/api/users/me/export', { token: idToken });
      if (!res.ok) {
        setMsg(res.networkError
          ? "couldn't reach server — try again"
          : `export failed (${res.status})`);
        return;
      }
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `studybuddy-export-${handle}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setMsg('export downloaded.');
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'export failed');
    } finally {
      setExporting(false);
    }
  }, [idToken, handle]);

  const onDelete = useCallback(async () => {
    if (!idToken) return;
    if (confirmText.trim().toLowerCase() !== handle.toLowerCase()) {
      setMsg('type your handle exactly to confirm.');
      return;
    }
    setDeleting(true);
    setMsg('');
    try {
      const res = await api('/api/users/me', { method: 'DELETE', token: idToken });
      if (!res.ok) {
        setMsg(res.networkError
          ? "couldn't reach server — try again"
          : `delete failed (${res.status})`);
        setDeleting(false);
        return;
      }
      // Local cleanup so the in-flight code / solved markers don't follow the
      // next user who logs in on this device. Best-effort; server side is
      // already authoritative.
      try { localStorage.clear(); } catch { /* ignore */ }
      signOut();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'delete failed');
      setDeleting(false);
    }
  }, [idToken, handle, confirmText, signOut]);

  return (
    <div
      className="sk-box shadow"
      style={{
        marginTop: 24,
        padding: 18,
        background: 'var(--paper)',
      }}
    >
      <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700 }}>manage your data</h3>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--ink-soft)' }}>
        Download a JSON copy of everything we have on you, or permanently
        close your account. See the{' '}
        <a href="/privacy" style={{ color: 'var(--w-coral)' }}>privacy policy</a>{' '}
        for what's included.
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          className="sk-btn ghost"
          onClick={onExport}
          disabled={exporting || !idToken}
          style={{ fontSize: 12 }}
        >
          {exporting ? 'exporting…' : 'export my data'}
        </button>
        {!showDeletePanel && (
          <button
            className="sk-btn ghost"
            onClick={() => { setShowDeletePanel(true); setMsg(''); }}
            disabled={!idToken}
            style={{ fontSize: 12, color: 'var(--w-coral)' }}
          >
            delete my account
          </button>
        )}
      </div>

      {showDeletePanel && (
        <div
          style={{
            marginTop: 14,
            padding: 12,
            background: 'var(--w-cream)',
            border: '1.4px solid var(--ink)',
            borderRadius: 6,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            permanently delete @{handle}?
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-soft)', lineHeight: 1.5, marginBottom: 10 }}>
            this wipes your account, solve history, friends, and Cognito
            login. it can't be undone. type <strong>@{handle.toLowerCase()}</strong>{' '}
            (without the @) below to confirm.
          </div>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={handle.toLowerCase()}
            disabled={deleting}
            style={{
              width: '100%',
              padding: '6px 10px',
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 12,
              border: '1.4px solid var(--ink)',
              borderRadius: 4,
              background: 'var(--paper)',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            <button
              className="sk-btn"
              onClick={() => { setShowDeletePanel(false); setConfirmText(''); setMsg(''); }}
              disabled={deleting}
              style={{ fontSize: 12 }}
            >
              cancel
            </button>
            <button
              className="sk-btn primary"
              onClick={onDelete}
              disabled={deleting || confirmText.trim().toLowerCase() !== handle.toLowerCase()}
              style={{ fontSize: 12, background: 'var(--w-coral)' }}
            >
              {deleting ? 'deleting…' : 'delete forever'}
            </button>
          </div>
        </div>
      )}

      {msg && (
        <div
          style={{
            marginTop: 10,
            fontSize: 11,
            color: 'var(--ink-soft)',
            fontFamily: 'JetBrains Mono, monospace',
          }}
        >
          {msg}
        </div>
      )}
    </div>
  );
};

export default AccountManagement;
